import type { TFunction } from "i18next";
import type { AppNotification } from "@/features/notifications/types";

export type FormattedNotification = {
  title: string;
  body: string;
  detail?: string;
};

type NotificationAudience = "user" | "staff";

const METADATA_STRING_FIELDS = [
  "roomName",
  "roomNames",
  "reservationTitle",
  "dateLabel",
  "timeSlot",
  "organizerName",
  "participantsCount",
  "serviceName",
  "providerName",
  "visitorName",
  "hostName",
  "companyName",
  "senderName",
  "parcelType",
  "recipientName",
  "parcelStatus",
  "parcelCount",
  "itemsSummary",
  "totalTtc",
  "itemCount",
  "status",
  "statusName",
  "categoryName",
  "locationName",
  "ticketTitle",
  "messageSubject",
  "messagePreview",
  "detail",
] as const;

function asString(value: unknown): string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed || undefined;
}

function inferHttpMethod(eventType: string): string {
  if (eventType.includes("Deleted") || eventType.includes("Cancelled")) {
    return "DELETE";
  }
  if (
    eventType.includes("Created") ||
    eventType.includes("Received") ||
    eventType.includes("messageReceive")
  ) {
    return "POST";
  }
  return "PATCH";
}

export function getNotificationHttpMethod(notification: AppNotification): string {
  const fromMeta = notification.metadata?.httpMethod;
  if (typeof fromMeta === "string" && fromMeta.trim()) {
    return fromMeta.trim().toUpperCase();
  }
  return inferHttpMethod(notification.event_type);
}

function getNotificationAudience(notification: AppNotification): NotificationAudience {
  const audience = notification.metadata?.audience;
  return audience === "staff" ? "staff" : "user";
}

function joinParts(parts: Array<string | undefined>, separator = " · "): string {
  return parts.filter((part): part is string => Boolean(part?.trim())).join(separator);
}

function buildDetailFallback(metadata: Record<string, unknown>): string | undefined {
  const roomName = asString(metadata.roomName);
  const dateLabel = asString(metadata.dateLabel);
  const timeSlot = asString(metadata.timeSlot);
  const serviceName = asString(metadata.serviceName);
  const providerName = asString(metadata.providerName);
  const visitorName = asString(metadata.visitorName);
  const hostName = asString(metadata.hostName);
  const companyName = asString(metadata.companyName);
  const senderName = asString(metadata.senderName);
  const parcelType = asString(metadata.parcelType);
  const recipientName = asString(metadata.recipientName);
  const itemsSummary = asString(metadata.itemsSummary);
  const totalTtc = asString(metadata.totalTtc);
  const status = asString(metadata.status) ?? asString(metadata.statusName);
  const categoryName = asString(metadata.categoryName);
  const locationName = asString(metadata.locationName);
  const ticketTitle = asString(metadata.ticketTitle);
  const messagePreview = asString(metadata.messagePreview);
  const messageSubject = asString(metadata.messageSubject);

  if (roomName || dateLabel || timeSlot) {
    return joinParts([roomName, dateLabel, timeSlot, asString(metadata.reservationTitle)]);
  }
  if (serviceName || providerName) {
    return joinParts([serviceName, providerName, dateLabel, timeSlot]);
  }
  if (visitorName || hostName) {
    return joinParts([
      visitorName,
      hostName ? `→ ${hostName}` : undefined,
      companyName,
      joinParts([dateLabel, timeSlot], " "),
    ]);
  }
  if (parcelType || senderName || recipientName) {
    return joinParts([
      parcelType,
      senderName ? `de ${senderName}` : undefined,
      recipientName ? `pour ${recipientName}` : undefined,
    ]);
  }
  if (itemsSummary || totalTtc || status) {
    return joinParts([
      itemsSummary,
      totalTtc ? `${totalTtc} € TTC` : undefined,
      status,
    ]);
  }
  if (categoryName || status || locationName) {
    return joinParts([categoryName, status, locationName]) ?? ticketTitle;
  }
  if (messagePreview || messageSubject) {
    return joinParts([
      senderName ? `De ${senderName}` : undefined,
      messagePreview ?? messageSubject,
    ], " — ");
  }
  return undefined;
}

function buildInterpolationContext(
  notification: AppNotification,
): Record<string, string> {
  const metadata = notification.metadata ?? {};
  const context: Record<string, string> = {};

  for (const field of METADATA_STRING_FIELDS) {
    context[field] = asString(metadata[field]) ?? "";
  }

  const roomName = context.roomName;
  const serviceName = context.serviceName;
  const visitorName = context.visitorName;
  const ticketTitle = context.ticketTitle;
  const messageSubject = context.messageSubject || asString(notification.title) || "";

  context.messageSubject = messageSubject;
  context.detail = context.detail || buildDetailFallback(metadata) || "";
  context.label = roomName || serviceName || visitorName || ticketTitle || "";
  context.roomSuffix = roomName ? ` · ${roomName}` : "";
  context.serviceSuffix = serviceName ? ` · ${serviceName}` : "";
  context.schedule = joinParts([context.dateLabel, context.timeSlot], " ");
  context.hostLine = context.hostName ? `→ ${context.hostName}` : "";

  return context;
}

function translateFeedPart(
  t: TFunction,
  key: string,
  context: Record<string, string>,
  fallback: string,
): string {
  const translated = t(key, { ...context, defaultValue: "" });
  if (typeof translated === "string" && translated.trim()) {
    return translated;
  }
  return fallback;
}

export function formatNotificationMessage(
  notification: AppNotification,
  t: TFunction,
): FormattedNotification {
  const audience = getNotificationAudience(notification);
  const method = getNotificationHttpMethod(notification);
  const entity = notification.entity_type || "default";
  const context = buildInterpolationContext(notification);

  const fallbackTitle = notification.title?.trim() || notification.event_type;
  const fallbackBody =
    context.detail
    || notification.body?.trim()
    || notification.event_type;

  const eventTitleKey = `notifications.feed.events.${notification.event_type}.${audience}.title`;
  const eventBodyKey = `notifications.feed.events.${notification.event_type}.${audience}.body`;
  const entityTitleKey = `notifications.feed.${entity}.${method}.${audience}.title`;
  const entityBodyKey = `notifications.feed.${entity}.${method}.${audience}.body`;

  const title = translateFeedPart(t, eventTitleKey, context, "")
    || translateFeedPart(t, entityTitleKey, context, "")
    || fallbackTitle;

  const body = translateFeedPart(t, eventBodyKey, context, "")
    || translateFeedPart(t, entityBodyKey, context, "")
    || fallbackBody;

  return {
    title,
    body,
    detail: context.detail || undefined,
  };
}
